import { ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router";
import { createSelector } from "reselect";
import { authInit, authRedirect } from "./features/auth/redux/auth.actions";
import { IRootState } from "./redux/root.reducer";
import { routes } from "./route.config";

export const selectAuthState = createSelector(
    (state: IRootState) => state.auth.isInitialized,
    (state: IRootState) => state.auth.isUserLoading,
    (state: IRootState) => state.auth.user,
    (isInitialized, isUserLoading, user) => ({
        isInitialized,
        isUserLoading,
        isAuthenticated: !!user,
    }),
);

const router = createBrowserRouter(
    routes.map(({ path, component: Component, isPublic }) => ({
        path,
        Component: isPublic
            ? Component
            : () => (
                  <ProtectedRoute>
                      <Component />
                  </ProtectedRoute>
              ),
    })),
);

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch();
    const { isInitialized, isUserLoading, isAuthenticated } =
        useSelector(selectAuthState);

    useEffect(() => {
        if (isInitialized && !isUserLoading && !isAuthenticated) {
            dispatch(authRedirect());
        }
    }, [isInitialized, isUserLoading, isAuthenticated, dispatch]);

    if (!isInitialized || isUserLoading) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <div>Authenticating...</div>;
    }

    return <>{children}</>;
};

function App() {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(authInit());
    }, []);

    return (
        <div className="App">
            <RouterProvider router={router} />
        </div>
    );
}

export default App;
